if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initDots();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      // Dots and arrows (Figma). One dot per image, over the bottom of the main image;
      // the arrows show only where there is somewhere to go. From 750px up only the
      // active image is shown and the thumbnails swap it, so the slider never sees
      // more than one slide; there the position is the slide that is active, and the
      // arrows are handled here. On mobile the slider scrolls and does its own arrows.
      initDots() {
        const viewer = this.elements.viewer;
        if (!viewer || !viewer.sliderItems || viewer.sliderItems.length < 2) return;

        const total = viewer.sliderItems.length;
        const container = document.createElement('div');
        container.className = 'product__media-dots';
        this.dots = [];

        for (let index = 0; index < total; index++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product__media-dot';
          dot.setAttribute('aria-label', `${index + 1} / ${total}`);
          dot.addEventListener('click', () => this.goToIndex(index));
          container.appendChild(dot);
          this.dots.push(dot);
        }
        viewer.appendChild(container);

        [viewer.prevButton, viewer.nextButton].forEach((button) => {
          if (!button) return;
          button.addEventListener(
            'click',
            (event) => {
              if (!this.mql.matches) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.goToIndex(this.getCurrentIndex() + (button === viewer.nextButton ? 1 : -1));
            },
            true
          );
        });

        viewer.addEventListener('slideChanged', () => this.updateIndicators());
        // On mobile the slider sets the arrows itself on every scroll, and it counts the
        // last slide as not yet in view; settle them once the scrolling stops.
        if (viewer.slider) viewer.slider.addEventListener('scroll', debounce(() => this.updateIndicators(), 120));
        this.mql.addEventListener('change', () => this.updateIndicators());
        this.updateIndicators();
      }

      getSlides() {
        return Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
      }

      getCurrentIndex() {
        if (this.mql.matches) {
          const index = this.getSlides().findIndex((slide) => slide.classList.contains('is-active'));
          return index < 0 ? 0 : index;
        }
        return Math.max(0, (this.elements.viewer.currentPage || 1) - 1);
      }

      goToIndex(index) {
        const slide = this.getSlides()[index];
        if (slide) this.setActiveMedia(slide.dataset.mediaId, false);
      }

      updateIndicators() {
        if (!this.dots) return;
        const current = this.getCurrentIndex();
        this.dots.forEach((dot, index) => {
          if (index === current) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });

        const { prevButton, nextButton } = this.elements.viewer;
        const last = this.dots.length - 1;
        if (prevButton) prevButton.toggleAttribute('disabled', current <= 0);
        if (nextButton) nextButton.toggleAttribute('disabled', current >= last);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY - 50;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateIndicators();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // The thumbnails are a Swiper; bring the current one into view when the image
        // changes from the arrows or the dots.
        const swiper = this.elements.thumbnails.swiper;
        if (swiper) {
          const box = this.elements.thumbnails.getBoundingClientRect();
          const rect = thumbnail.getBoundingClientRect();
          const inView = rect.top >= box.top - 1 && rect.bottom <= box.bottom + 1 && rect.left >= box.left - 1 && rect.right <= box.right + 1;
          if (!inView) {
            const index = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).indexOf(thumbnail);
            if (index >= 0) swiper.slideTo(index);
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
